import { useCallback, useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { OnboardingLiveCreationIntentItemFragment } from "../../gql/graphql.ts";
import { BasicTable } from "../../UI/Table/BasicTable.tsx";
import { EmptyTableMessage } from "../../UI/Table/EmptyTableMessage.tsx";
import {
  SortLabels,
  TableColumnHeader,
} from "../../UI/Table/TableColumnHeader.tsx";
import { compareAlphabetically } from "../../UI/Table/compareAlphabetically.ts";
import { uniqueSortedOptions } from "../../UI/Table/Filters/uniqueSortedOptions.ts";
import {
  checkboxListFilterProps,
  dateRangeFilterProps,
  numberRangeFilterProps,
  sortableColumnProps,
} from "../../UI/Table/tableColumnHeaderProps.tsx";
import { useAllUserGroups } from "../../UserGroup/useAllUserGroups.ts";
import { formatSlot } from "../onboardingLiveTime.ts";
import { CreationIntentStatusBadge } from "../CreationIntentStatusBadge.tsx";
import { creationIntentStatusLabels } from "../creationIntentStatusLabels.ts";
import {
  CREATION_INTENT_STATUSES_IN_LIFECYCLE_ORDER,
  creationIntentStatusRank,
  matchesOnboardingLivePlansFilters,
  OnboardingLivePlansFilters,
  OnboardingLivePlansSort,
  OnboardingLivePlansSortField,
} from "./onboardingLivePlansTableState.ts";
import {
  ALPHABETICAL_SORT_LABELS,
  COUNT_SORT_LABELS,
  DATE_SORT_LABELS,
} from "./onboardingLivesSortLabels.ts";

const STATUS_SORT_LABELS: SortLabels = {
  asc: "Bozza → Annullata",
  desc: "Annullata → Bozza",
};

const STATUS_OPTIONS = CREATION_INTENT_STATUSES_IN_LIFECYCLE_ORDER.map(
  (status) => ({ value: status, label: creationIntentStatusLabels[status] }),
);

type OnboardingLivePlanRow = {
  intent: OnboardingLiveCreationIntentItemFragment;
  userGroupName: string;
};

type Props = {
  intents: OnboardingLiveCreationIntentItemFragment[];
  sort: OnboardingLivePlansSort;
  onSortChange: (sort: OnboardingLivePlansSort) => void;
  filters: OnboardingLivePlansFilters;
  onFiltersChange: (filters: OnboardingLivePlansFilters) => void;
  onOpen: (intent: OnboardingLiveCreationIntentItemFragment) => void;
};

export function OnboardingLivePlansTable({
  intents,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  onOpen,
}: Props) {
  const { userGroups } = useAllUserGroups();
  const userGroupNameOf = useCallback(
    (userGroupId: string) =>
      userGroups.find((g) => g.id === userGroupId)?.name ?? userGroupId,
    [userGroups],
  );
  const visibleRows = useMemo(
    () =>
      intents
        .filter((intent) => matchesOnboardingLivePlansFilters(intent, filters))
        .map((intent) => ({
          intent,
          userGroupName: userGroupNameOf(intent.userGroupId),
        })),
    [intents, filters, userGroupNameOf],
  );
  const columns = useMemo(() => {
    const creatorOptions = uniqueSortedOptions(
      intents.flatMap((intent) =>
        intent.createdByEmail
          ? [{ value: intent.createdByEmail, label: intent.createdByEmail }]
          : [],
      ),
    );
    const userGroupOptions = uniqueSortedOptions(
      intents.map((intent) => ({
        value: intent.userGroupId,
        label: userGroupNameOf(intent.userGroupId),
      })),
    );
    const updateFilters = (changes: Partial<OnboardingLivePlansFilters>) =>
      onFiltersChange({ ...filters, ...changes });
    const sortableBy = (
      field: OnboardingLivePlansSortField,
      sortLabels: SortLabels,
    ) => sortableColumnProps({ field, sort, onSortChange, sortLabels });

    const columnHelper = createColumnHelper<OnboardingLivePlanRow>();
    return [
      columnHelper.accessor((row) => row.intent.createdAtUtc, {
        id: "createdAt",
        sortingFn: "basic",
        header: () => (
          <TableColumnHeader
            label="Creata il"
            {...sortableBy("createdAt", DATE_SORT_LABELS)}
            {...dateRangeFilterProps(filters.createdAt, (createdAt) =>
              updateFilters({ createdAt }),
            )}
          />
        ),
        cell: (info) => (
          <span className={"whitespace-nowrap"}>
            {formatSlot(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.intent.createdByEmail ?? undefined, {
        id: "createdBy",
        sortingFn: compareAlphabetically,
        sortUndefined: "last",
        header: () => (
          <TableColumnHeader
            label="Creata da"
            {...sortableBy("createdBy", ALPHABETICAL_SORT_LABELS)}
            {...checkboxListFilterProps(
              creatorOptions,
              filters.creatorEmails,
              (creatorEmails) => updateFilters({ creatorEmails }),
            )}
          />
        ),
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("userGroupName", {
        id: "userGroup",
        sortingFn: compareAlphabetically,
        header: () => (
          <TableColumnHeader
            label="User group"
            {...sortableBy("userGroup", ALPHABETICAL_SORT_LABELS)}
            {...checkboxListFilterProps(
              userGroupOptions,
              filters.userGroupIds,
              (userGroupIds) => updateFilters({ userGroupIds }),
            )}
          />
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor((row) => row.intent.slots.length, {
        id: "slots",
        header: () => (
          <TableColumnHeader
            label="Slot"
            {...sortableBy("slots", COUNT_SORT_LABELS)}
            {...numberRangeFilterProps(filters.slots, (slots) =>
              updateFilters({ slots }),
            )}
          />
        ),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor(
        (row) => creationIntentStatusRank(row.intent.status),
        {
          id: "status",
          header: () => (
            <TableColumnHeader
              label="Stato"
              {...sortableBy("status", STATUS_SORT_LABELS)}
              {...checkboxListFilterProps(
                STATUS_OPTIONS,
                filters.statuses,
                (statuses) => updateFilters({ statuses }),
              )}
            />
          ),
          cell: (info) => (
            <CreationIntentStatusBadge
              status={info.row.original.intent.status}
            />
          ),
        },
      ),
    ];
  }, [intents, sort, onSortChange, filters, onFiltersChange, userGroupNameOf]);

  const sorting = useMemo(
    () => [{ id: sort.sortBy, desc: sort.sortDirection === "desc" }],
    [sort],
  );

  const table = useReactTable({
    columns,
    data: visibleRows,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (intents.length <= 0)
    return <EmptyTableMessage message={"Nessuna pianificazione."} />;

  return (
    <>
      <BasicTable
        table={table}
        onRowClick={(row) => onOpen(row.intent)}
        scrollableMinWidthClassName={"min-w-[720px]"}
      />
      {visibleRows.length <= 0 ? (
        <EmptyTableMessage
          message={"Nessuna pianificazione corrisponde ai filtri."}
        />
      ) : null}
    </>
  );
}
